import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Flag } from "lucide-react";
import { HOUSES } from "@/data/content";
import { Reveal, SectionHead } from "@/components/Reveal";
import Parallax from "@/components/Parallax";

function FlipCard({ h }) {
  const [flipped, setFlipped] = useState(false);
  const canHover = typeof window !== "undefined" && !!window.matchMedia?.("(hover: hover)").matches;
  return (
    <motion.div
      className="perspective-1200 h-[360px] w-full cursor-pointer will-change-transform sm:w-[300px]"
      data-testid={`house-card-${h.name.toLowerCase()}`}
      onClick={() => { if (!canHover) setFlipped((f) => !f); }}
      onMouseEnter={() => canHover && setFlipped(true)}
      onMouseLeave={() => canHover && setFlipped(false)}
      whileHover={{ y: -10 }}
      transition={{ type: "spring", stiffness: 200, damping: 18 }}
    >
      <motion.div
        className="preserve-3d relative h-full w-full"
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="backface-hidden glass absolute inset-0 flex flex-col items-center justify-center rounded-3xl p-8 text-center">
          <span
            className={`flex h-20 w-20 items-center justify-center rounded-full font-display text-2xl font-bold ${h.on === "navy" ? "text-navy" : "text-ivory"}`}
            style={{ background: h.color, boxShadow: `0 0 50px ${h.color}66` }}
          >
            {h.name[0]}
          </span>
          <h3 className="mt-6 font-display text-xl font-bold tracking-widest text-fg">{h.name.toUpperCase()}</h3>
          <span className="mt-2 font-serif2 text-sm italic text-soft">{h.tag}</span>
          <span className="mt-6 flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] text-gold">
            <Flag className="h-3 w-3" /> {h.founded ? `EST. ${h.founded}` : "JUNIOR WING"}
          </span>
          <span className="mt-6 text-[10px] uppercase tracking-[0.3em] text-soft/70">Hover or tap to reveal</span>
        </div>

        <div
          className="backface-hidden glass absolute inset-0 flex flex-col rounded-3xl p-7 [transform:rotateY(180deg)]"
          style={{ borderColor: `${h.color}55` }}
        >
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 shrink-0 rounded-full" style={{ background: h.color, boxShadow: `0 0 24px ${h.color}55` }} />
            <div>
              <h4 className="font-display text-lg font-bold text-fg">{h.name} House</h4>
              <span className="text-[10px] uppercase tracking-[0.25em] text-soft">{h.founded ? `${h.tag} · Est. ${h.founded}` : `${h.tag} · Junior Wing`}</span>
            </div>
          </div>
          <p className="mt-4 flex-1 font-serif2 text-[15px] italic leading-relaxed text-fg/85" data-testid={`house-spirit-${h.name.toLowerCase()}`}>
            {h.spirit}
          </p>
          <ul className="space-y-1.5">
            {h.known.map((k, i) => (
              <li key={i} className="flex items-start gap-2 text-xs leading-relaxed text-soft">
                <Check className="mt-0.5 h-3 w-3 shrink-0" style={{ color: h.color }} /> {k}
              </li>
            ))}
          </ul>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function Houses() {
  return (
    <section id="houses" className="relative overflow-hidden py-24 lg:py-32">
      <Parallax speed={90}>
        <div className="pointer-events-none absolute left-1/2 top-0 h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-forest/10 blur-[140px]" />
      </Parallax>
      <div className="mx-auto max-w-7xl px-6">
        <SectionHead
          eyebrow="The House System"
          title="The Five Houses of Doon"
          sub="Five banners since 1935, a fifth since 1991 — plus Foot and Martyn, the holding houses where every Dosco's journey begins. Hover or tap a crest to flip it."
        />
        <div className="flex flex-wrap justify-center gap-6">
          {HOUSES.map((h, i) => (
            <Reveal key={h.name} delay={i * 0.08} y={36} className="w-full sm:w-auto">
              <FlipCard h={h} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
